import type { InstallationDraft } from 'types/installationPresentation';
import { parseInstallationDraft } from './installationPresentation';
import { hasApprovedCheckoutTitles } from './checkoutTitlePolicy';

export interface PersistedInstallationDraft {
  storeId: string;
  revision: number;
  preferences: InstallationDraft;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

// JSONB cannot store unpaired UTF-16 surrogates. Keep the pure C1 parser intact.
const representable = (value: unknown): boolean => {
  if (typeof value === 'string') {
    for (let index = 0; index < value.length; index++) {
      const code = value.charCodeAt(index);
      if (code === 0 || (code >= 0xdc00 && code <= 0xdfff)) return false;
      if (code >= 0xd800 && code <= 0xdbff) {
        const next = value.charCodeAt(++index);
        if (!(next >= 0xdc00 && next <= 0xdfff)) return false;
      }
    }
  } else if (isRecord(value)) {
    return Object.values(value).every(representable);
  }
  return true;
};

const readRepresentableDraft = (value: unknown): InstallationDraft | null => {
  const draft = parseInstallationDraft(value);
  return draft && representable(draft) ? draft : null;
};

export const prepareInstallationDraftForPersistence = (value: unknown): InstallationDraft | null => {
  const draft = readRepresentableDraft(value);
  return draft && hasApprovedCheckoutTitles(draft) ? draft : null;
};

export const parseInstallationDraftRecord = (
  value: unknown,
  storeId: string,
): PersistedInstallationDraft | null => {
  if (!isRecord(value) || value.storeId !== storeId ||
    typeof value.revision !== 'number' || !Number.isInteger(value.revision) || value.revision < 1) return null;
  const preferences = readRepresentableDraft(value.preferences);
  return preferences ? { storeId, revision: value.revision, preferences } : null;
};

const canonical = (value: unknown): unknown => isRecord(value)
  ? Object.fromEntries(Object.keys(value).sort().map((key) => [key, canonical(value[key])]))
  : value;

const confirms = (record: PersistedInstallationDraft | null, submitted: unknown) => {
  const draft = prepareInstallationDraftForPersistence(submitted);
  return record && draft && JSON.stringify(canonical(record.preferences)) === JSON.stringify(canonical(draft))
    ? record : null;
};

export const confirmInstallationDraftCreate = (
  value: unknown,
  storeId: string,
  submitted: unknown,
): PersistedInstallationDraft | null => {
  const record = parseInstallationDraftRecord(value, storeId);
  return record?.revision === 1 ? confirms(record, submitted) : null;
};

export const confirmInstallationDraftSave = (
  value: unknown,
  storeId: string,
  revision: number,
  submitted: unknown,
): PersistedInstallationDraft | null => {
  if (!isRecord(value) || value.affected_rows !== 1 || !Array.isArray(value.returning) ||
    value.returning.length !== 1 || !Number.isInteger(revision) || revision < 1) return null;
  const record = parseInstallationDraftRecord(value.returning[0], storeId);
  return record?.revision === revision + 1 ? confirms(record, submitted) : null;
};
